import { useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { EntityCard, EntityList, SectionCard } from '@/components/DataList';
import { distributor, tahweel } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  Form,
  Input,
  Loading,
  Modal,
  Select,
} from '@/components/ui';
import { required, str, uuid } from '@/utils/format';

function platformCustomerId(c: JsonMap): string {
  return str(c.customer_user_id ?? c.user_id ?? '');
}

function platformCustomerLabel(c: JsonMap): string {
  const name = str(c.nickname ?? c.name);
  return `${name} (${str(c.phone)})`;
}

export function DistributorScreen() {
  const state = useAsync(
    async () => {
      const [pkgs, platform, phones] = await Promise.all([
        distributor.packages(),
        distributor.customers(),
        tahweel.customers(),
      ]);
      if (!pkgs.success) return pkgs;
      return {
        success: true,
        packages: pickList(pkgs, 'packages', 'items', 'data'),
        platformCustomers: platform.success
          ? pickList(platform, 'customers', 'items', 'data')
          : [],
        savedPhones: phones.success ? pickList(phones, 'customers', 'items', 'data') : [] };
    },
    (p) => ({
      packages: Array.isArray(p.packages) ? (p.packages as JsonMap[]) : pickList(p, 'packages'),
      platformCustomers: Array.isArray(p.platformCustomers)
        ? (p.platformCustomers as JsonMap[])
        : pickList(p, 'platformCustomers', 'customers'),
      savedPhones: Array.isArray(p.savedPhones)
        ? (p.savedPhones as JsonMap[])
        : pickList(p, 'savedPhones', 'customers') }),
  );

  const [pkgId, setPkgId] = useState('');
  const [phone, setPhone] = useState('');
  const [savedPhoneId, setSavedPhoneId] = useState('');
  const [customerUserId, setCustomerUserId] = useState('');
  const [topupAmount, setTopupAmount] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const [savePhoneOpen, setSavePhoneOpen] = useState(false);
  const [savePhoneName, setSavePhoneName] = useState('');
  const [savePhoneNumber, setSavePhoneNumber] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createPassword, setCreatePassword] = useState('');

  function pickSavedPhone(id: string) {
    setSavedPhoneId(id);
    const row = (state.data?.savedPhones ?? []).find((c) => str(c.id) === id);
    if (row) setPhone(str(row.phone));
  }

  async function placeOrder() {
    const miss = required({ pkgId, phone }, ['pkgId', 'phone']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const selected = (state.data?.savedPhones ?? []).find((c) => str(c.id) === savedPhoneId);
    const res = await distributor.order(
      {
        package_id: Number(pkgId) || pkgId,
        target_phone: phone,
        customer_name: str(selected?.name, phone),
        payment_type: 'cash',
      },
      uuid(),
    );
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم شحن الرقم.') : errorMessage(res));
  }

  async function savePhoneNumberToBook() {
    const miss = required({ savePhoneName, savePhoneNumber }, ['savePhoneName', 'savePhoneNumber']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await tahweel.saveCustomer({ name: savePhoneName, phone: savePhoneNumber });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setSavePhoneOpen(false);
    setSavePhoneName('');
    setSavePhoneNumber('');
    setMsg('تم حفظ الرقم في أرقام الشحن.');
    state.reload();
  }

  async function topup() {
    const miss = required({ customerUserId, topupAmount }, ['customerUserId', 'topupAmount']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await distributor.topupCustomer({
      customer_user_id: Number(customerUserId) || customerUserId,
      amount: Number(topupAmount) });
    setBusy(false);
    setMsg(res.success ? 'تم شحن محفظة زبون المنصة.' : errorMessage(res));
    if (res.success) state.reload();
  }

  async function createPlatformCustomer() {
    const miss = required(
      { createName, createPhone, createPassword },
      ['createName', 'createPhone', 'createPassword'],
    );
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await distributor.addCustomer({
      name: createName.trim(),
      phone: createPhone,
      password: createPassword });
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    setCreateOpen(false);
    setCreateName('');
    setCreatePhone('');
    setCreatePassword('');
    setMsg(typeof res.message === 'string' ? res.message : 'تم إنشاء زبون المنصة.');
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const packages = state.data?.packages ?? [];
  const platformCustomers = state.data?.platformCustomers ?? [];
  const savedPhones = state.data?.savedPhones ?? [];

  return (
    <ScreenFrame mode="customer" title="شحن زبون">
      <SectionCard title="شحن وحدات لرقم">
        <Form onSubmit={() => void placeOrder()}>
          <Select
            label="الباقة"
            value={pkgId}
            onChange={setPkgId}
            options={[
              { value: '', label: 'اختر باقة' },
              ...packages.map((p) => ({
                value: str(p.id ?? p.package_id),
                label: `${str(p.label ?? p.name)} — ${str(p.price)}` })),
            ]}
            required
          />
          <Select
            label="رقم محفوظ (اختياري)"
            value={savedPhoneId}
            onChange={pickSavedPhone}
            options={[
              { value: '', label: 'إدخال يدوي' },
              ...savedPhones.map((c) => ({
                value: str(c.id),
                label: `${str(c.name)} (${str(c.phone)})` })),
            ]}
          />
          <Input
            label="هاتف الشحن"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setSavedPhoneId('');
            }}
            inputMode="tel"
            required
          />
          <div className="btn-row">
            <Button type="submit" disabled={busy}>
              تنفيذ الشحن
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setSavePhoneNumber(phone);
                setSavePhoneOpen(true);
              }}
            >
              حفظ الرقم في أرقام الشحن
            </Button>
          </div>
        </Form>
      </SectionCard>

      <SectionCard title="شحن محفظة زبون منصة">
        <Form onSubmit={() => void topup()}>
          <Select
            label="زبون المنصة"
            value={customerUserId}
            onChange={setCustomerUserId}
            options={[
              { value: '', label: 'اختر زبون منصة' },
              ...platformCustomers.map((c) => ({
                value: platformCustomerId(c),
                label: platformCustomerLabel(c) })),
            ]}
            required
          />
          <Input
            label="المبلغ"
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            inputMode="decimal"
            required
          />
          <div className="btn-row">
            <Button type="submit" disabled={busy}>
              شحن المحفظة
            </Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setCreateOpen(true)}>
              إنشاء زبون منصة
            </Button>
          </div>
        </Form>
      </SectionCard>

      <SectionCard title="زبائن المنصة المرتبطون">
        {platformCustomers.length === 0 ? (
          <Empty title="لا يوجد زبائن منصة مرتبطون" />
        ) : (
          <EntityList>
            {platformCustomers.map((c) => (
              <EntityCard
                key={platformCustomerId(c) || str(c.id)}
                title={str(c.nickname ?? c.name)}
                meta={str(c.phone)}
                amount={c.wallet_balance ?? c.balance}
              />
            ))}
          </EntityList>
        )}
      </SectionCard>

      {msg ? <p className="ok">{msg}</p> : null}

      <Modal open={savePhoneOpen} title="حفظ رقم في أرقام الشحن" onClose={() => setSavePhoneOpen(false)}>
        <Form onSubmit={() => void savePhoneNumberToBook()}>
          <Input
            label="الاسم"
            value={savePhoneName}
            onChange={(e) => setSavePhoneName(e.target.value)}
            required
          />
          <Input
            label="الهاتف"
            value={savePhoneNumber}
            onChange={(e) => setSavePhoneNumber(e.target.value)}
            inputMode="tel"
            required
          />
          <Button type="submit" disabled={busy}>
            حفظ الرقم
          </Button>
        </Form>
      </Modal>

      <Modal open={createOpen} title="إنشاء زبون منصة" onClose={() => setCreateOpen(false)}>
        <Form onSubmit={() => void createPlatformCustomer()}>
          <Input
            label="الاسم"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            required
          />
          <Input
            label="الهاتف"
            value={createPhone}
            onChange={(e) => setCreatePhone(e.target.value)}
            inputMode="tel"
            required
          />
          <Input
            label="كلمة المرور"
            type="password"
            value={createPassword}
            onChange={(e) => setCreatePassword(e.target.value)}
            required
          />
          <p className="card-meta">
            ينشئ حساب زبون جديد تابع لك. إذا كان الرقم مسجّلاً مسبقاً سيظهر خطأ.
          </p>
          <Button type="submit" disabled={busy}>
            إنشاء الحساب
          </Button>
        </Form>
      </Modal>
    </ScreenFrame>
  );
}
